import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  useTheme,
  useMediaQuery,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { motion } from 'framer-motion';

const MotionButton = motion(Button);

const navItems = [
  { label: 'Home', path: '/v2', description: 'Go to homepage' },
  { label: 'About', path: '/v2/about', description: 'Learn more about Agustí Conesa' },
  { label: 'Portfolio', path: '/v2/portfolio', description: 'View my projects and work' },
  { label: 'Resume', path: '/v2/resume', description: 'See my professional experience' },
  { label: 'Blog', path: '/v2/blog', description: 'Read my articles and insights' },
  { label: 'Contact', path: '/v2/contact', description: 'Get in touch with me' },
];

const Navbar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNavigation = (path) => {
    navigate(path);
    handleClose();
  };

  return (
    <AppBar 
      position="sticky" 
      component="nav"
      aria-label="Main navigation"
      sx={{ 
        background: 'rgba(16, 18, 27, 0.8)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
      }}
    >
      <Toolbar>
        <Typography
          variant="h6"
          component="div"
          sx={{ 
            flexGrow: 1,
            fontWeight: 700,
            cursor: 'pointer',
            '&:hover': {
              color: theme.palette.primary.main,
            },
          }}
          onClick={() => navigate('/v2')}
          role="banner"
          aria-label="Agustí Conesa's Portfolio"
        >
          AC
        </Typography>

        {isMobile ? (
          <>
            <IconButton
              edge="end"
              color="inherit"
              aria-label="Open navigation menu"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
            >
              <MenuIcon />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              role="menu"
              aria-label="Navigation menu"
            >
              {navItems.map((item) => (
                <MenuItem
                  key={item.path}
                  onClick={() => handleNavigation(item.path)}
                  selected={location.pathname === item.path}
                  role="menuitem"
                  aria-label={item.description}
                >
                  {item.label}
                </MenuItem>
              ))}
            </Menu>
          </>
        ) : (
          <Box 
            component="nav" 
            sx={{ display: 'flex', gap: 2 }}
            aria-label="Main navigation links"
          >
            {navItems.map((item) => (
              <MotionButton
                key={item.path}
                color="inherit"
                onClick={() => navigate(item.path)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                aria-label={item.description}
                aria-current={location.pathname === item.path ? 'page' : undefined}
                sx={{
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    width: location.pathname === item.path ? '100%' : '0%',
                    height: '2px',
                    bottom: 0,
                    left: 0,
                    backgroundColor: theme.palette.primary.main,
                    transition: 'width 0.3s ease-in-out',
                  },
                  '&:hover::after': {
                    width: '100%',
                  },
                }}
              >
                {item.label}
              </MotionButton>
            ))}
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Navbar; 